import { tap } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';

import { Injectable } from '@angular/core';

import { Action, createSelector, NgxsOnInit, Selector, State, StateContext, Store } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';

import { RuleTypesApiService } from '@app/core/api/customers/criterion-addition/rule-types-api.service';
import { UserLanguage } from '@app/core/models/user.models';
import { CustomerState } from '@app/core/store/states/customer/customer.state';
import { UserState } from '@app/core/store/states/user/user.state';
import { isArrayShallowEqual } from '@app/core/utilities/array.utilities';
import { RuleType, SupportedCriteria } from '../shared/models/rule.models';
import { Types2SupportedCriteria } from '../shared/utilities/rules.utilities';
import { CRITERION_ADDITION_STATE, CriterionAdditionStateModel } from './criterion-addition-state.models';
import { GetRuleTypes, GetSupportedCriteria } from './criterion-addition.actions';

@State({
  name: CRITERION_ADDITION_STATE,
  defaults: {
    supportedCriteria: null,
    ruleTypes: null,
  },
})
@Injectable()
export class CriterionAdditionState implements NgxsOnInit {
  static ruleType(ruleTypeId: number) {
    return createSelector([CriterionAdditionState.ruleTypes], (ruleTypes: RuleType[]) =>
      ruleTypes.find((ruleType) => ruleType.ruleTypeId === ruleTypeId),
    );
  }

  @Selector()
  static ruleTypes({ ruleTypes }: CriterionAdditionStateModel): RuleType[] {
    return ruleTypes;
  }

  @Selector()
  static supportedCriteria({ supportedCriteria }: CriterionAdditionStateModel): SupportedCriteria[] {
    return supportedCriteria;
  }

  @Selector([UserState.userLocale])
  static resourceLink(userLocale: UserLanguage): string {
    const resourceLinks = {
      EN: 'https://support.adspert.net/en/articles/8644337-when-does-adspert-create-manual-campaigns-amazon-ads-only',
      DE: 'https://support.adspert.net/de/articles/8644337-wann-erstellt-adspert-manuelle-kampagnen-nur-amazon-ads',
      ZH: 'https://support.adspert.net/zh-CN/articles/8644337-adspert-%E4%BD%95%E6%97%B6%E5%88%9B%E5%BB%BA%E6%89%8B%E5%8A%A8%E5%B9%BF%E5%91%8A%E7%B3%BB%E5%88%97-%E4%BB%85%E9%99%90%E4%BA%9A%E9%A9%AC%E9%80%8A%E5%B9%BF%E5%91%8A',
    };

    return resourceLinks[userLocale] || resourceLinks.EN;
  }

  constructor(
    private rtas: RuleTypesApiService,
    private store: Store,
  ) {}

  ngxsOnInit(): void {
    this.store.dispatch(new GetRuleTypes());
  }

  @Action(GetRuleTypes)
  GetRuleTypes({ setState }: StateContext<CriterionAdditionStateModel>) {
    return this.store.select(CustomerState.summary).pipe(
      map((summary) => summary?.accountPlatforms || []),
      filter((accountPlatforms) => accountPlatforms.length > 0),
      distinctUntilChanged(isArrayShallowEqual),
      switchMap((accountPlatforms) => this.rtas.getTypes({ platform: accountPlatforms })),
      tap((ruleTypes) => setState(patch({ ruleTypes }))),
    );
  }

  @Action(GetSupportedCriteria)
  GetSupportedCriteria({ setState }: StateContext<CriterionAdditionStateModel>) {
    const summary = this.store.selectSnapshot(CustomerState.summary);
    return this.rtas
      .getTypes({})
      .pipe(
        tap((ruleTypes) =>
          setState(patch({ supportedCriteria: Types2SupportedCriteria(ruleTypes, summary.accountPlatforms) })),
        ),
      );
  }
}
